import { Box, styled } from "@mui/material";
import { Card } from "@ui";
import { tokens } from "@styles/tokens";

/**
 * Shared building blocks for the auth form cards (login, sign-up, …). Keeping
 * the glass card, divider, and link styling here means every auth form stays
 * visually identical without duplicating the styled definitions.
 */

// The fixed content width every auth page shares (card + header + badges).
export const AUTH_CARD_WIDTH = 440;

// Viewport width at which auth switches from the mobile stack to the desktop
// split-screen. Shared so the layout branch and the card styling agree.
export const AUTH_SPLIT_WIDTH = 900;

// Card — glass (translucent + blur) over the mobile hero; flat on a surface in
// the desktop split-screen, where the panel itself provides the backdrop.
export const AuthCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: AUTH_CARD_WIDTH,
  borderRadius: 28,
  background: theme.skuey.glassBg,
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  border: `1px solid ${theme.skuey.glassBorder}`,
  boxShadow: "0 8px 30px rgba(15,23,42,0.06), 0 20px 80px rgba(15,23,42,0.08)",
  padding: tokens.space(7),
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(3),
  [`@media (min-width:${AUTH_SPLIT_WIDTH}px)`]: {
    background: theme.skuey.surface,
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    border: `1px solid ${theme.skuey.border}`,
    boxShadow: tokens.shadow.md,
    borderRadius: tokens.radius.xl,
  },
}));

// "or" separator between social and email sign-in.
export const AuthDivider = () => (
  <DividerRoot>
    <span>or</span>
  </DividerRoot>
);

const DividerRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: tokens.space(3),
  color: theme.skuey.subtle,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  "&::before, &::after": {
    content: '""',
    flex: 1,
    height: 1,
    backgroundColor: theme.skuey.border,
  },
}));

// Inline brand link rendered as a button (forgot-password, switch auth page).
export const AuthLink = styled("button")(({ theme }) => ({
  border: "none",
  background: "none",
  padding: 0,
  cursor: "pointer",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.brand[600],
  "&:hover": { color: theme.skuey.brand[700] },
}));

// Centered helper line beneath the form (e.g. "Don't have an account?").
export const AuthFooterText = styled("p")(({ theme }) => ({
  margin: 0,
  textAlign: "center",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.muted,
}));
